import { Icon } from '@mgmresorts/mgm-ui';
import React, { useEffect, useRef } from 'react';

import {
  StyledModalOverlay,
  StyledModal,
  StyledCloseButton,
  StyledModalContent,
  StyledModalHeader,
  StyledModalTitle,
  StyledMessageSection,
  StyledModalMessage,
  StyledModalBody,
  StyledModalActions,
  StyledSecondaryButton,
  StyledPrimaryButton,
} from './styles';

export interface ConfirmModalAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  testId?: string;
}

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message?: React.ReactNode;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
  showCloseButton?: boolean;
  maxWidth?: string;
  isLoading?: boolean;
  testId?: string;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onClose,
  onConfirm,
  showCloseButton = true,
  maxWidth,
  isLoading = false,
  testId = 'confirm-modal',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management and keyboard handling
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element to restore later
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Set initial focus to cancel button after modal opens
      setTimeout(() => {
        if (cancelButtonRef.current) {
          cancelButtonRef.current.focus();
        }
      }, 100);

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && !isLoading) {
          onClose();
          return;
        }

        // Focus trap - Tab key handling
        if (event.key === 'Tab') {
          const modal = modalRef.current;
          if (!modal) return;

          const focusableElements = modal.querySelectorAll(
            'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          );

          const focusableArray = Array.from(focusableElements) as HTMLElement[];
          const firstElement = focusableArray[0];
          const lastElement = focusableArray[focusableArray.length - 1];

          if (event.shiftKey) {
            // Shift + Tab: if we're on first element, go to last
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement?.focus();
            }
          } else {
            // Tab: if we're on last element, go to first
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement?.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';

        // Restore focus to the element that was focused before modal opened
        if (previousFocusRef.current && previousFocusRef.current.focus) {
          previousFocusRef.current.focus();
        }
      };
    }
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && !isLoading) {
      onClose();
    }
  };

  const titleId = `${testId}-title`;
  const messageId = `${testId}-message`;

  return (
    <StyledModalOverlay onClick={handleOverlayClick} data-testid={`${testId}-overlay`}>
      <StyledModal
        ref={modalRef}
        role='dialog'
        aria-labelledby={titleId}
        aria-describedby={message || children ? messageId : undefined}
        data-testid={testId}
        maxWidth={maxWidth}
      >
        <div style={{ position: 'relative' }}>
          {showCloseButton && (
            <StyledCloseButton
              onClick={onClose}
              disabled={isLoading}
              aria-label='Close modal'
              data-testid={`${testId}-close-button`}
            >
              <Icon name='symbol-x' variant='outlined' size='small' aria-hidden='true' />
            </StyledCloseButton>
          )}

          <StyledModalContent showCloseButton={showCloseButton}>
            <StyledModalHeader>
              <StyledModalTitle id={titleId} variant='heading-medium-1' as='h2'>
                {title}
              </StyledModalTitle>
            </StyledModalHeader>

            <StyledMessageSection>
              {message && (
                <StyledModalMessage id={messageId} variant='body-regular-m'>
                  {message}
                </StyledModalMessage>
              )}

              {children && <StyledModalBody id={children ? messageId : undefined}>{children}</StyledModalBody>}
            </StyledMessageSection>

            <StyledModalActions>
              <StyledPrimaryButton
                variant='secondary'
                onClick={onConfirm}
                loading={isLoading}
                testId={`${testId}-confirm-button`}
              >
                {confirmLabel}
              </StyledPrimaryButton>
              <StyledSecondaryButton
                ref={cancelButtonRef}
                variant='primary'
                onClick={onClose}
                disabled={isLoading}
                testId={`${testId}-cancel-button`}
              >
                {cancelLabel}
              </StyledSecondaryButton>
            </StyledModalActions>
          </StyledModalContent>
        </div>
      </StyledModal>
    </StyledModalOverlay>
  );
};
